import React, { useState, useEffect } from 'react';
import { getRandomLoadingMessage } from '@/utils/loadingMessages';

interface LoadingScreenProps {
  message?: string;
  fullScreen?: boolean;
}

/**
 * Schermata di caricamento minimal con spinner professionale
 * Stile coerente con il loader iniziale in index.html
 */
export const LoadingScreen: React.FC<LoadingScreenProps> = ({ 
  message = 'Caricamento...', 
  fullScreen = true 
}) => {
  const [funnyMessage, setFunnyMessage] = useState(getRandomLoadingMessage());

  // Cambia messaggio ogni 3 secondi
  useEffect(() => {
    const interval = setInterval(() => {
      setFunnyMessage(getRandomLoadingMessage());
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className={`
      ${fullScreen ? 'fixed inset-0' : 'absolute inset-0'}
      flex flex-col items-center justify-center
      bg-gradient-to-br from-white to-zinc-50
      dark:from-zinc-900 dark:to-zinc-800
      z-50 transition-all duration-300
    `}>
      {/* Spinner professionale */}
      <div className="w-12 h-12 border-3 border-zinc-200 dark:border-zinc-700 border-t-zinc-600 dark:border-t-zinc-400 rounded-full animate-spin mb-6" />

      {/* Messaggio di caricamento */}
      <p className="text-sm font-medium text-zinc-600 dark:text-zinc-400 tracking-wide mb-2">
        {message}
      </p>

      {/* Messaggio simpatico che cambia */}
      <p className="text-xs text-zinc-500 dark:text-zinc-500 italic max-w-md text-center transition-opacity duration-500">
        {funnyMessage}
      </p>
    </div>
  );
};

/**
 * Loading inline per componenti più piccoli
 */
export const LoadingInline: React.FC<{ message?: string }> = ({ message = 'Caricamento...' }) => {
  return (
    <div className="flex items-center justify-center space-x-3 p-8">
      {/* Spinner piccolo */}
      <div className="w-6 h-6 border-2 border-zinc-200 dark:border-zinc-700 border-t-zinc-600 dark:border-t-zinc-400 rounded-full animate-spin" />
      
      <span className="text-sm text-zinc-600 dark:text-zinc-400">
        {message}
      </span>
    </div>
  );
};

/**
 * Skeleton loader per dashboard
 */
export const DashboardSkeleton: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-white to-zinc-50 dark:from-zinc-900 dark:to-zinc-800 p-6">
      <div className="space-y-6 animate-pulse">
        {/* Header skeleton */}
        <div className="flex items-center justify-between">
          <div className="h-8 w-64 bg-zinc-200 dark:bg-zinc-700 rounded" />
          <div className="h-10 w-32 bg-zinc-200 dark:bg-zinc-700 rounded" />
        </div>

        {/* KPI Cards skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-32 bg-zinc-100 dark:bg-zinc-800 rounded-xl" />
          ))}
        </div>

        {/* Charts skeleton */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="h-80 bg-zinc-100 dark:bg-zinc-800 rounded-xl" />
          <div className="h-80 bg-zinc-100 dark:bg-zinc-800 rounded-xl" />
        </div>
      </div>
    </div>
  );
};

export default LoadingScreen;
